import {
  Box,
  Button,
  Container,
  Heading,
  HStack,
  Icon,
  SimpleGrid,
  Text,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import { IconType } from "react-icons";
import {
  FiActivity,
  FiArrowLeft,
  FiBarChart2,
  FiBox,
  FiBriefcase,
  FiMonitor,
  FiShield,
  FiSliders,
  FiTrendingUp,
} from "react-icons/fi";
import { PageHero } from "../components/common/PageHero";
import { SectionWrapper } from "../components/common/SectionWrapper";
import { Footer } from "../components/landing/Footer";
import { Navbar } from "../components/landing/Navbar";

const managerSpecs = [
  { label: "نام مدیر سبدگردان", value: "شرکت تأمین سرمایه امید" },
  { label: "شماره ثبت", value: "۴۱۸۲۲۹" },
  { label: "شنایه ملی", value: "۱۰۳۲۰۷۰۱۲۳۶" },
  { label: "کد پستی", value: "۱۵۵۷۷۴۶۵۱۱" },
  { label: "شماره ثبت نزد سازمان بورس و اوراق بهادار", value: "۱۰۹۳۵" },
  {
    label: "تاریخ اخذ مجوز سبدگردانی از سازمان بورس و اوراق بهادار",
    value: "۱۳۹۲/۰۲/۲۴",
  },
  { label: "شماره مجوز سبدگردانی", value: "۱۲۱/۲۲۱۱۸۰" },
  { label: "شماره تماس", value: "۰۲۱۵۵۴۹۰۶", href: "tel:021554906" },
];

const steps = [
  {
    label: "گام اول",
    title: "مراجعه به سبدگردان",
    desc: "شروع فرایند با ثبت درخواست و برقراری ارتباط اولیه با تیم سبدگردانی اختصاصی.",
  },
  {
    label: "گام دوم",
    title: "تکمیل فرم قرارداد",
    desc: "تکمیل اطلاعات، ارزیابی ریسک‌پذیری و نهایی‌سازی چارچوب همکاری متناسب با نیاز سرمایه‌گذار.",
  },
  {
    label: "گام سوم",
    title: "واریز وجه قرارداد یا تحویل دارایی",
    desc: "انتقال وجه به حساب معرفی‌شده یا تحویل دارایی‌های بورسی برای آغاز تشکیل سبد اختصاصی.",
  },
  {
    label: "گام چهارم",
    title: "ارسال مدارک به سازمان بورس",
    desc: "ثبت و ارسال مستندات لازم برای طی‌کردن الزامات نظارتی و فعال‌سازی فرایند رسمی سبدگردانی.",
  },
  {
    label: "گام پنجم",
    title: "صدور کد سبد اختصاصی و شروع سرمایه‌گذاری",
    desc: "پس از صدور کد، مدیریت پرتفوی آغاز شده و سبد مطابق استراتژی توافق‌شده اجرا می‌شود.",
  },
];

const benefitRows = [
  [
    {
      icon: FiTrendingUp,
      text: "مدیریت کارآمد و اثر‌بخش در سبد دارایی‌ها",
    },
    {
      icon: FiActivity,
      text: "جلوگیری از قرار گرفتن سرمایه در معرض رفتارهای هیجانی بازار",
    },
    {
      icon: FiShield,
      text: "وجود ساز‌و‌کار نظارتی دقیق مطابق قرارداد سبدگردانی",
    },
    {
      icon: FiSliders,
      text: "امکان تشکیل سبد منطبق بر ویژگی‌ها و ترجیحات هر سرمایه‌گذار",
    },
  ],
  [
    {
      icon: FiBarChart2,
      text: "کاهش ریسک سرمایه‌گذاری در مقایسه با سرمایه‌گذاری مستقیم",
    },
    {
      icon: FiBriefcase,
      text: "پیگیری سود مجامع و اعمال حق رای به نمایندگی از سرمایه‌گذار",
    },
    {
      icon: FiMonitor,
      text: "امکان اطلاع از ترکیب سبد و عملکرد آن از طریق سایت سبدگردان",
    },
  ],
];

const sectionNavItems = [
  { href: "#dp-intro", label: "معرفی" },
  { href: "#dp-manager", label: "مدیر سبد" },
  { href: "#dp-steps", label: "مراحل" },
  { href: "#dp-benefits", label: "مزایا" },
  { href: "#dp-conflict", label: "تضاد منافع" },
  { href: "#dp-cta", label: "سامانه" },
];

const SectionIntro = ({
  icon,
  title,
  description,
  accent,
}: {
  icon: IconType;
  title: string;
  description: string;
  accent: string;
}) => (
  <VStack align="center" spacing={4} textAlign="center">
    <Box
      w={{ base: "14", md: "16" }}
      h={{ base: "14", md: "16" }}
      borderRadius="2xl"
      bg={`${accent}14`}
      color={accent}
      display="flex"
      alignItems="center"
      justifyContent="center"
      boxShadow={`0 18px 40px ${accent}22`}
    >
      <Icon as={icon as any} boxSize={{ base: 6, md: 7 }} />
    </Box>
    <VStack align="center" spacing={2} maxW="760px">
      <Heading as="h2" fontSize={{ base: "xl", md: "2xl" }}>
        {title}
      </Heading>
      <Text color="gray.500" _dark={{ color: "gray.300" }} lineHeight="1.9">
        {description}
      </Text>
    </VStack>
    <Box
      h="1px"
      w="100%"
      bg={`linear-gradient(90deg, transparent, ${accent}, transparent)`}
    />
  </VStack>
);

export const DedicatedPortfolioPage = () => {
  const cardBg = useColorModeValue("white", "dark.50");
  const altCardBg = useColorModeValue(
    "rgba(255,255,255,0.72)",
    "rgba(255,255,255,0.04)",
  );
  const textColor = useColorModeValue("gray.800", "white");
  const subtitleColor = useColorModeValue("gray.600", "gray.300");
  const borderColor = useColorModeValue("gray.100", "gray.700");
  const jumpBg = useColorModeValue("gray.50", "whiteAlpha.100");
  const jumpHoverBg = useColorModeValue("red.50", "rgba(239,68,68,0.18)");
  const jumpBorder = useColorModeValue("gray.200", "whiteAlpha.200");
  const iconBg = useColorModeValue("red.50", "rgba(239,68,68,0.14)");
  const softCardBg = useColorModeValue("gray.50", "whiteAlpha.50");
  const introGlow = useColorModeValue(
    "radial-gradient(circle at top right, rgba(239,68,68,0.12), transparent 35%)",
    "radial-gradient(circle at top right, rgba(248,113,113,0.12), transparent 35%)",
  );
  return (
    <Box dir="rtl" overflowX="hidden">
      <Navbar />

      <PageHero
        title="سبدگردانی"
        titleHighlight="اختصاصی"
        subtitle="تشکیل سبدی از دارایی‌های مالی بر مبنای ترجیحات شما - اهداف، محدودیت‌ها، افق زمانی و بودجه - با کد معاملاتی مختص سبدگردانی."
        primaryAction={{
          label: "خدمات",
          onClick: () => {
            const section = document.getElementById("dp-intro");
            section?.scrollIntoView({ behavior: "smooth", block: "start" });
          },
        }}
      />

      <Box
        position="sticky"
        top={{ base: "72px", md: "88px" }}
        zIndex={20}
        backdropFilter="blur(12px)"
        borderBottom="1px solid"
        borderColor={jumpBorder}
      >
        <Container maxW="container.xl" py={3}>
          <HStack spacing={2} wrap="wrap" justify="center">
            {sectionNavItems.map((item) => (
              <Button
                key={item.href}
                as="a"
                href={item.href}
                size="sm"
                variant="ghost"
                bg={jumpBg}
                border="1px solid"
                borderColor={jumpBorder}
                borderRadius="full"
                _hover={{ bg: jumpHoverBg }}
              >
                {item.label}
              </Button>
            ))}
          </HStack>
        </Container>
      </Box>

      <SectionWrapper id="dp-intro" py={{ base: 10, md: 14 }}>
        <Box>
          <Container maxW="container.xl">
            <VStack align="stretch" spacing={6}>
              <SectionIntro
                icon={FiActivity}
                title="معرفی سبدگردانی اختصاصی"
                description="خدمتی برای طراحی، اجرا و پایش یک سبد سرمایه‌گذاری که بر اساس اهداف، محدودیت‌ها و سطح ریسک‌پذیری هر سرمایه‌گذار شکل می‌گیرد."
                accent="#ef4444"
              />

              <VStack align="stretch" spacing={6}>
                <Box
                  bg={cardBg}
                  borderRadius="2xl"
                  border="1px solid"
                  borderColor={borderColor}
                  p={{ base: 5, md: 6 }}
                  position="relative"
                  overflow="hidden"
                >
                  <Box
                    position="absolute"
                    inset={0}
                    bg={introGlow}
                    pointerEvents="none"
                  />
                  <VStack align="stretch" spacing={4}>
                    <Text
                      color={subtitleColor}
                      lineHeight="2.1"
                      textAlign="justify"
                    >
                      سبدگردانی فرایند خرید و فروش اوراق بهادار به نمایندگی از
                      سرمایه‌گذار در قالب کد معاملاتی مختص سبدگردانی می‌باشد. در
                      این فرایند خبرگان مالی اقدام به تشکیل سبدی از دارایی‌های
                      مالی برای سرمایه‌گذار بر مبنای ترجیحات وی شامل اهداف،
                      محدودیت‌ها، افق زمانی و بودجه می‌نماید. سبد سرمایه‌گذاری
                      بطور مستمر تحت نظارت کارشناسان قرار داشته و متناسب با
                      تغییر متغیرهای کلان اقتصادی و فضای سرمایه‌گذاری نسبت به
                      اعمال تغییر در ترکیب دارایی‌ها اقدام می‌گردد. هدف نهایی در
                      فرایند سبدگردانی افزایش ارزش سبد از طریق حداکثر نمودن
                      بازدهی با پذیرش سطح مشخصی از ریسک متناسب با ترجیحات
                      سرمایه‌گذار است. مهم‌ترین تفاوت سبدگردان‌ها با سایر
                      نهادهای مالی اداره کننده وجوه (بانک یا صندوق سرمایه‌گذاری)
                      این است که سبدگردان‌ها بیش از محصول محور بودن، مشتری محور
                      هستند. این بدان معنی است که سبدگردان در ابتدا با ارزیابی
                      ظرفیت ریسک‌پذیری و اهداف هر مشتری، نیازهای سرمایه‌گذاری وی
                      را مشخص می‌نماید و سپس اقدام به تشکیل سبد اختصاصی برای وی
                      می‌نماید.
                    </Text>
                    <Text
                      color={subtitleColor}
                      lineHeight="2.1"
                      textAlign="justify"
                    >
                      شرکت تامین سرمایه امید در راستای تکمیل زنجیره خدمات مدیریت
                      دارایی، مقتضی به نیاز بازار و تقاضای مشتریان اقدام به
                      راه‌اندازی واحد سبدگردانی نموده است. در واحد سبدگردانی پس
                      از بررسی منویات مشتریان در خصوص سرمایه‌گذاری و بررسی اهداف
                      مدنظر آن‌ها اقدام به تهیه بیانیه سرمایه‌گذاری می‌شود.
                      مشتریان در این فرآیند قادر به دریافت خدمات مشاوره حسب نیاز
                      خود می‌باشند. جهت جلب اطمینان مشتریان از ارائه خدمات در
                      قالب قرارداد، امین سبدگردان بر فعالیت این واحد نظارت دارد.
                    </Text>
                  </VStack>
                </Box>

                <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={4}>
                  {[
                    {
                      value: "۱۳۹۲/۰۲/۲۴",
                      helper:
                        "تاریخ اخذ مجوز سبدگردانی از سازمان بورس و اوراق بهادار",
                    },
                    {
                      value: "۱۲۱/۲۲۱۱۸۰",
                      helper: "شماره مجوز سبدگردانی",
                    },
                    {
                      value: "مشتری‌محور",
                      helper:
                        "«سبدگردان‌ها بیش از محصول محور بودن، مشتری محور هستند.»",
                    },
                    {
                      value: "امین",
                      helper: "«امین سبدگردان بر فعالیت این واحد نظارت دارد.»",
                    },
                  ].map((item) => (
                    <Box
                      key={item.value}
                      bg={altCardBg}
                      borderRadius="2xl"
                      border="1px solid"
                      borderColor={borderColor}
                      p={5}
                      textAlign="center"
                      backdropFilter="blur(10px)"
                      minH="100%"
                    >
                      <VStack spacing={2}>
                        <Text fontSize="2xl" fontWeight="black" color="red.400">
                          {item.value}
                        </Text>
                        <Text
                          color={subtitleColor}
                          fontSize="sm"
                          lineHeight="1.7"
                        >
                          {item.helper}
                        </Text>
                      </VStack>
                    </Box>
                  ))}
                </SimpleGrid>
              </VStack>
            </VStack>
          </Container>
        </Box>
      </SectionWrapper>

      <SectionWrapper id="dp-manager" alt>
        <Container maxW="container.xl">
          <VStack align="stretch" spacing={6}>
            <SectionIntro
              icon={FiBriefcase}
              title="اطلاعات مدیر سبدگردانی"
              description="مشخصات ثبتی، مجوزها و اطلاعات تماس مدیر سبدگردانی برای شفافیت بیشتر و ارزیابی دقیق‌تر توسط سرمایه‌گذاران."
              accent="#f59e0b"
            />

            <Box
              bg={cardBg}
              borderRadius="3xl"
              border="1px solid"
              borderColor={borderColor}
              p={{ base: 4, md: 5 }}
            >
              <SimpleGrid columns={{ base: 1, sm: 2, xl: 4 }} spacing={4}>
                {managerSpecs.map((spec) => (
                  <Box
                    key={spec.label}
                    borderRadius="xl"
                    bg={softCardBg}
                    border="1px solid"
                    borderColor={borderColor}
                    p={4}
                    minH="100%"
                  >
                    <Text fontSize="sm" color={subtitleColor} mb={2}>
                      {spec.label}
                    </Text>
                    {spec.href ? (
                      <Text
                        as="a"
                        href={spec.href}
                        color={textColor}
                        fontWeight="bold"
                      >
                        {spec.value}
                      </Text>
                    ) : (
                      <Text color={textColor} fontWeight="bold">
                        {spec.value}
                      </Text>
                    )}
                  </Box>
                ))}
              </SimpleGrid>
            </Box>
          </VStack>
        </Container>
      </SectionWrapper>

      <SectionWrapper id="dp-steps">
        <Container maxW="container.xl">
          <VStack align="stretch" spacing={6}>
            <SectionIntro
              icon={FiBarChart2}
              title="مراحل تشکیل سبد"
              description="فرایند تشکیل سبد اختصاصی به‌صورت مرحله‌به‌مرحله و شفاف انجام می‌شود تا از شروع همکاری تا آغاز سرمایه‌گذاری، مسیر مشخصی پیش روی سرمایه‌گذار باشد."
              accent="#ef4444"
            />
            <Box
              bg={cardBg}
              borderRadius="3xl"
              border="1px solid"
              borderColor={borderColor}
              p={{ base: 5, md: 7 }}
            >
              <VStack spacing={0} align="stretch" position="relative">
                <Box
                  position="absolute"
                  right="11px"
                  top="8px"
                  bottom="8px"
                  w="2px"
                  bg={borderColor}
                />

                {steps.map((step, index) => (
                  <HStack
                    key={step.title}
                    spacing={4}
                    align="flex-start"
                    py={4}
                  >
                    <Box position="relative" zIndex={1} flexShrink={0}>
                      <Box
                        w="24px"
                        h="24px"
                        borderRadius="full"
                        bg="red.400"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Box w="10px" h="10px" borderRadius="full" bg="white" />
                      </Box>
                    </Box>

                    <VStack align="flex-start" spacing={1} flex={1}>
                      <Box bg={iconBg} px={2.5} py={0.5} borderRadius="md">
                        <Text fontSize="xs" fontWeight="bold" color="red.500">
                          {step.label}
                        </Text>
                      </Box>
                      <Text fontWeight="bold" fontSize="sm" color={textColor}>
                        {step.title}
                      </Text>
                      <Text
                        fontSize="sm"
                        color={subtitleColor}
                        lineHeight="1.9"
                      >
                        {step.desc}
                      </Text>
                    </VStack>
                  </HStack>
                ))}
              </VStack>
            </Box>
          </VStack>
        </Container>
      </SectionWrapper>

      <SectionWrapper id="dp-benefits" alt>
        <Container maxW="container.xl">
          <VStack align="stretch" spacing={6}>
            <SectionIntro
              icon={FiBox}
              title="مزایای سبدهای اختصاصی"
              description="سبد اختصاصی با تکیه بر مدیریت حرفه‌ای، نظارت مداوم و شخصی‌سازی استراتژی، مزایایی فراتر از سرمایه‌گذاری مستقیم در اختیار سرمایه‌گذار قرار می‌دهد."
              accent="#f59e0b"
            />

            <VStack spacing={4} align="stretch">
              <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={4}>
                {benefitRows[0].map((benefit) => (
                  <Box
                    key={benefit.text}
                    bg={cardBg}
                    borderRadius="2xl"
                    border="1px solid"
                    borderColor={borderColor}
                    p={5}
                    _hover={{ transform: "translateY(-4px)", boxShadow: "xl" }}
                    transition="all 0.25s ease"
                  >
                    <VStack align="center" spacing={4} textAlign="center">
                      <Box
                        w={{ base: "14", md: "16" }}
                        h={{ base: "14", md: "16" }}
                        borderRadius="2xl"
                        bg="rgba(245,158,11,0.10)"
                        color="#f59e0b"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        // boxShadow="0 18px 40px rgba(245,158,11,0.16)"
                      >
                        <Icon
                          as={benefit.icon as any}
                          boxSize={{ base: 6, md: 7 }}
                        />
                      </Box>
                      <Text color={subtitleColor} lineHeight="1.9">
                        {benefit.text}
                      </Text>
                    </VStack>
                  </Box>
                ))}
              </SimpleGrid>

              <SimpleGrid
                columns={{ base: 1, md: 3 }}
                spacing={4}
                maxW="1000px"
                mx="auto"
                w="100%"
              >
                {benefitRows[1].map((benefit) => (
                  <Box
                    key={benefit.text}
                    bg={cardBg}
                    borderRadius="2xl"
                    border="1px solid"
                    borderColor={borderColor}
                    p={5}
                    _hover={{ transform: "translateY(-4px)", boxShadow: "xl" }}
                    transition="all 0.25s ease"
                  >
                    <VStack align="center" spacing={4} textAlign="center">
                      <Box
                        w={{ base: "14", md: "16" }}
                        h={{ base: "14", md: "16" }}
                        borderRadius="2xl"
                        bg="rgba(245,158,11,0.10)"
                        color="#f59e0b"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        // boxShadow="0 18px 40px rgba(245,158,11,0.16)"
                      >
                        <Icon
                          as={benefit.icon as any}
                          boxSize={{ base: 6, md: 7 }}
                        />
                      </Box>
                      <Text color={subtitleColor} lineHeight="1.9">
                        {benefit.text}
                      </Text>
                    </VStack>
                  </Box>
                ))}
              </SimpleGrid>
            </VStack>
          </VStack>
        </Container>
      </SectionWrapper>

      <SectionWrapper id="dp-conflict">
        <Container maxW="container.xl">
          <VStack align="stretch" spacing={6}>
            <SectionIntro
              icon={FiShield}
              title="تضاد منافع"
              description="چارچوب‌های نظارتی و قراردادی سبدگردانی به‌گونه‌ای طراحی شده‌اند که منافع سرمایه‌گذار در اولویت قرار گیرد و تضادهای احتمالی به‌صورت شفاف مدیریت شوند."
              accent="#ef4444"
            />

            <Box
              bg={cardBg}
              borderRadius="3xl"
              border="1px solid"
              borderColor={borderColor}
              p={{ base: 6, md: 8 }}
            >
              <Text color={subtitleColor} lineHeight="2.1" textAlign="justify">
                تضاد منافعی که ممکن است به دلیل ارائه‌ی خدمات مختلف بین منافع
                سبدگردان یا اشخاص وابسته و کارکنان وی با مشتریان به‌وجود آید و
                راه‌های کنترل و حل و فصل این تضاد منافع به شرح زیر می‌باشد: در
                این خصوص شرکت تامین سرمایه امید طبق ماده ۱۴ دستورالعمل سبدگردانی
                عمل کرده و در صورتی که تضاد منافع بین سبدگردان و مشتری به وجود
                آید، اولویت با مشتری می‌باشد. در صورتی که سبدگردان یا کارکنان وی
                ذینفع باشند، مطابق ماده ۲۷ دستورالعمل سبدگردان،گزارش آن را افشا
                می‌نمایند. گزارش ذینفعان با توجه به الزامات تعیین شده در ماده۲۷
                دستورالعمل تاسیس و فعالیت سبد‌گردان، ابلاغی از سوی سازمان بورس و
                اوراق بهادار مبنی بر ارائه گزارش ادواری در مورد ذینفعان که در
                جهت اعمال نظارت بر فعالیت سبدگردان و کنترل تضاد منافع احتمالی
                بین شرکت سبد‌گردان و سرمایه‌گذاران مصوب گشته‌است، شرکت تامین
                سرمایه امید گزارش خود را به صورت دوره‌ای بر اساس ماده مذکور و در
                جهت اعتماد‌سازی و رضایتمندی ذینفعان پس از شروع به کار شرکت در
                این قسمت ارائه خواهد نمود.
              </Text>
            </Box>
          </VStack>
        </Container>
      </SectionWrapper>

      <SectionWrapper id="dp-cta">
        <Container maxW="container.lg">
          <Box
            bgGradient="linear(to-br, red.700, red.400)"
            borderRadius="3xl"
            p={{ base: 8, md: 12 }}
            color="white"
            textAlign="center"
            position="relative"
            overflow="hidden"
          >
            <Box
              position="absolute"
              inset="auto auto -80px -40px"
              w="220px"
              h="220px"
              borderRadius="full"
              bg="rgba(255,255,255,0.10)"
              filter="blur(10px)"
            />
            <Box
              position="absolute"
              inset="-50px -30px auto auto"
              w="180px"
              h="180px"
              borderRadius="full"
              bg="rgba(255,255,255,0.08)"
              filter="blur(8px)"
            />
            <VStack spacing={5}>
              <Heading as="h3" fontSize={{ base: "xl", md: "2xl" }}>
                سامانه اطلاعات سبد و ارتباط مشتریان
              </Heading>
              <Button
                as="a"
                href="https://omidwebsite.rhpco.ir"
                target="_blank"
                rel="noopener noreferrer"
                bg="white"
                color="red.600"
                rightIcon={<Icon as={FiBox as any} />}
                _hover={{ bg: "whiteAlpha.900" }}
                size="lg"
                borderRadius="xl"
              >
                ورود به سامانه
              </Button>
            </VStack>
          </Box>
        </Container>
      </SectionWrapper>

      <Footer />
    </Box>
  );
};
